import React, { FC, FormHTMLAttributes, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { Button, Heading } from 'components/ui';
import WebhooksStatistics from './components/WebhooksStatistics/WebhooksStatistics';
import { AppsConstants } from '../store/constants';
import { useDispatchRequest, useQuery } from '@redux-requests/react';
import { deleteAppHook, saveAppHook } from '../store/actions/';
import WebhookForm from './components/WebhookForm/WebhookForm';
import WebhookInfo from './components/WebhookInfo/WebhookInfo';
import { t, urlRegex } from 'utils';
import { getAppHooks } from 'modules/userApps/store/actions/getAppHooks';
// import { getAppHooksLogsStatistics } from 'modules/userApps/store/actions/getAppHooksLogsStatistics';

import styles from './AppWebhooks.module.scss';

export interface IAppWebhooks extends FormHTMLAttributes<HTMLFormElement> {
  id: string;
}

export interface IHook {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
  events: any[];
  secret: string;
  enableSourceEvents: boolean;
  secretHeader: string;
  appId?: string;
  uuid: string;
}

export interface INewHook extends Omit<IHook, 'id'> {}

const AppWebhooks: FC<IAppWebhooks> = (props) => {
  const { id, className } = props;
  const dispatch = useDispatchRequest();
  const {
    data: { hooks, eventsStatistics },
  } = useQuery({ type: AppsConstants.APP, requestKey: id });
  const [newHooks, setNewHooks] = useState<INewHook[]>([]);
  const isEmptyWebhooks = !hooks.length && !newHooks.length;
  const createWebhookButtonText = isEmptyWebhooks
    ? 'create.your.first.webhook.subscription'
    : 'add.another.webhook.subscription';
  const rootClassName = classNames(styles.root, className, {
    [styles.emptyWebhook]: isEmptyWebhooks,
  });
  const createEmptyHook = useCallback(
    (): INewHook => ({
      name: '',
      events: [],
      url: '',
      secret: '',
      enableSourceEvents: false,
      secretHeader: '',
      isActive: false,
      uuid: uuidv4(),
    }),
    []
  );
  // extract hook types so we can match them with API hook types statistics response to show statistics only fo hooks that are part of this app
  const hookTypes = hooks
    .reduce((allHooks: [], hook: { events: any }) => {
      const { events } = hook;
      const eventTypes = events.reduce((eventTypes: [], event: { name: string; types: any }) => {
        const eventTypeHooks = event?.types
          .filter(Boolean)
          .map((type: string) => `${event.name.toLowerCase()}.${type.toLowerCase()}`);
        return eventTypes.concat(eventTypeHooks);
      }, []);
      if (eventTypes.length > 0) {
        return allHooks.concat(eventTypes);
      } else {
        return allHooks;
      }
    }, [])
    .filter((val: any, index: number, self: any) => self.indexOf(val) === index);

  useEffect(() => {
    // dispatch(getAppHooksLogsStatistics(id, 'WEBHOOK_EVENTS_STATISTICS')).catch((e) =>
    //   console.error(e)
    // );
    dispatch(getAppHooks(id)).catch((e) => console.error(e));
  }, [dispatch, id]);

  const handleAddSubscription = useCallback(() => {
    setNewHooks((prev) => [...prev, createEmptyHook()]);
  }, [createEmptyHook]);

  const onRemoveHookSubscription = useCallback(
    async (hookId) => {
      const result = await dispatch(deleteAppHook(hookId, id))?.catch();
      if (!result) return t('errors.somethingWentWrong');
    },
    [dispatch, id]
  );

  const onRemoveNewHookSubscription = useCallback(
    (uuid) => setNewHooks((prev) => prev.filter((hook) => hook.uuid !== uuid)),
    []
  );

  const onSubscriptionSubmit = useCallback(
    async (values) => {
      const { error } = await dispatch(saveAppHook(values, id));
      return error?.data?.errors;
    },
    [dispatch, id]
  );
  // validation function
  const formValidate = useCallback((values: { [key: string]: any }): any => {
    const {
      // id,
      url,
      name,
      events,
      secret,
      secretHeader,
      isCustomHeader,
    } = values;
    const errors: any = {};
    if (!name?.length) errors.name = t('errors.required');
    if (!events?.length) errors.events = t('errors.required');
    if (!secret?.length) errors.secret = t('errors.required');
    if (!url?.match(urlRegex)) errors.url = t('errors.url.invalid');
    if (isCustomHeader && !secretHeader?.length) errors.url = t('errors.required');
    // return empty object if no errors
    const hasErrors =
      Object.keys(errors).length > 0 ||
      errors?.allowedCountries?.reduce(
        (hasError: boolean, countryErrObject: { [key: string]: string }) => {
          return hasError || Object.keys(countryErrObject).length > 0;
        },
        false
      );
    return hasErrors ? errors : {};
  }, []);

  const onNewSubscriptionSubmit = useCallback(
    async (values) => {
      // validate form
      const errors = formValidate(values);
      if (Object.keys(errors).length > 0) return errors;
      const { uuid } = values;
      const { error } = await dispatch(saveAppHook({ ...values, id: null }, id));
      if (!error) {
        // delete newHook as it's saved now
        const updatedNewHooks = newHooks.filter((newHook) => newHook.uuid === uuid);
        setNewHooks(updatedNewHooks);
      }
      return error?.data?.errors;
    },
    [dispatch, id, newHooks, formValidate]
  );

  return (
    <div className={rootClassName}>
      <div className={styles.content}>
        <Heading className='mb-2' icon='bullhorn'>
          {t('webhook.subscriptions')}
        </Heading>
        <WebhookInfo className={styles.info} />
        {hooks.map((hook: IHook) => (
          <WebhookForm
            {...hook}
            onSubscriptionSubmit={onSubscriptionSubmit}
            onRemoveSubscription={onRemoveHookSubscription}
            key={hook.id}
            hasDeleteButton
          />
        ))}
        {newHooks.map((hook: INewHook) => (
          <WebhookForm
            {...hook}
            id={hook.uuid}
            onSubscriptionSubmit={onNewSubscriptionSubmit}
            onRemoveSubscription={onRemoveNewHookSubscription}
            key={hook.uuid}
          />
        ))}
        <Button
          onClick={handleAddSubscription}
          icon='plus'
          type='button'
          variant='ghost'
          color='dark'
        >
          {t(createWebhookButtonText)}
        </Button>
      </div>
      <div className={styles.aside}>
        <WebhooksStatistics
          hookTypes={hookTypes}
          eventsStatistics={eventsStatistics}
          className='mb-4'
        />
      </div>
    </div>
  );
};

export default AppWebhooks;
